import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

const WhopCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing your login...');

  useEffect(() => {
    handleWhopCallback();
  }, []);

  const handleWhopCallback = async () => {
    try {
      const code = searchParams.get('code');
      const oauthError = searchParams.get('error');
      
      if (oauthError) {
        throw new Error(`Whop OAuth error: ${oauthError}`);
      }
      
      if (!code) {
        throw new Error('No authorization code received from Whop');
      }
      
      setStatus('Verifying with Whop...');
      console.log('Exchanging OAuth code with Whop...');
      
      // Exchange code for access token with Whop
      const tokenResponse = await fetch('https://api.whop.com/v5/oauth/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code,
          client_id: import.meta.env.VITE_WHOP_CLIENT_ID,
          client_secret: import.meta.env.VITE_WHOP_CLIENT_SECRET,
          grant_type: 'authorization_code',
          redirect_uri: `${window.location.origin}/whop-callback`
        })
      });
      
      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.text();
        console.error('Token exchange failed:', errorData);
        throw new Error('Failed to exchange code with Whop');
      }
      
      const { access_token } = await tokenResponse.json();
      console.log('✓ Got Whop access token');
      
      setStatus('Getting your account info...');
      
      // Get user info from Whop
      const userResponse = await fetch('https://api.whop.com/v5/me', {
        headers: {
          'Authorization': `Bearer ${access_token}`
        }
      });
      
      if (!userResponse.ok) {
        throw new Error('Failed to get user info from Whop');
      }
      
      const whopUser = await userResponse.json();
      console.log('✓ Got Whop user info:', whopUser.id);
      
      setStatus('Creating your session...');
      
      // Check if user exists in our database by Whop user ID
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('id, email, onboarding_completed, industry')
        .eq('whop_user_id', whopUser.id)
        .single();
      
      if (profile) {
        // User exists - sign them in with their email using magic link (OTP)
        console.log('✓ Profile found, sending OTP...');
        
        const { error: otpError } = await supabase.auth.signInWithOtp({
          email: profile.email,
          options: {
            shouldCreateUser: false
          }
        });
        
        if (otpError) {
          console.error('OTP error:', otpError);
        }
        
        // Since we can't auto-login without password, store Whop data and redirect
        sessionStorage.setItem('whop_user_id', whopUser.id);
        sessionStorage.setItem('whop_login_pending', 'true');
        
        setStatus('Login successful! Redirecting...');
        
        // Redirect to dashboard
        setTimeout(() => navigate('/app/dashboard'), 1000);
      } else {
        // New user - store their Whop info and send to registration
        console.log('⚠️ No profile found, user needs to complete registration');
        sessionStorage.setItem('whop_user_id', whopUser.id);
        sessionStorage.setItem('whop_email', whopUser.email);
        sessionStorage.setItem('whop_username', whopUser.username || whopUser.name);
        
        setStatus('Setting up your account...');
        setTimeout(() => navigate('/authentication?mode=register&from=whop'), 1000);
      }

    } catch (err) {
      console.error('Whop callback error:', err);
      setError(err.message || 'Something went wrong');
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center bg-card rounded-2xl shadow-xl p-8 border border-border">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-foreground mb-3">
            Authentication Failed
          </h2>
          
          <p className="text-muted-foreground mb-6">
            {error}
          </p>

          <button
            onClick={() => navigate('/authentication')}
            className="w-full px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center bg-card rounded-2xl shadow-xl p-8 border border-border">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-foreground font-medium mb-2">{status}</p>
        <p className="text-sm text-muted-foreground">Please wait...</p>
      </div>
    </div>
  );
};

export default WhopCallback;
